import React, { useCallback, useEffect, useState } from 'react';

import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Modal from '../../../components/common/Modal';
import StatusBadge from '../../../components/common/StatusBadge';
import {
  getSecurityOverviewApi,
  getLockedUsersApi,
  getUnlockRequestsApi,
  approveUnlockRequestApi,
  rejectUnlockRequestApi,
  unlockUserApi,
  resetUserTwoFactorApi,
  getLoginLogsApi,
} from '../../../api/security.api';

const fmt = (d) => {
  try {
    return new Intl.DateTimeFormat('fa-IR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(d));
  } catch {
    return String(d);
  }
};

const ROLE = { ADMIN: 'مدیر سیستم', CLUB_MANAGER: 'مدیر باشگاه', REFEREE: 'داور', OBSERVER: 'ناظر' };
const REQ_STATUS = { PENDING: 'در انتظار', APPROVED: 'تأیید شده', REJECTED: 'رد شده' };
const REQ_BADGE = { PENDING: 'pending', APPROVED: 'active', REJECTED: 'suspended' };

function Stat({ label, value, tone }) {
  return (
    <div className={`rounded-2xl p-4 ${tone}`}>
      <div className="text-3xl font-black">{value ?? '—'}</div>
      <div className="mt-1 text-xs font-bold opacity-70">{label}</div>
    </div>
  );
}

export default function SecurityCenter() {
  const [overview, setOverview] = useState(null);
  const [locked, setLocked] = useState([]);
  const [requests, setRequests] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: 'success' });

  const [decision, setDecision] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [busy, setBusy] = useState(false);

  const notify = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: 'success' }), 5000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, l, r, g] = await Promise.all([
        getSecurityOverviewApi(),
        getLockedUsersApi(),
        getUnlockRequestsApi({ limit: 20 }),
        getLoginLogsApi({ status: 'FAILED', limit: 15 }),
      ]);
      setOverview(o.data.data);
      setLocked(l.data.data || []);
      setRequests(r.data.data || []);
      setLogs(g.data.data || []);
    } catch {
      notify('دریافت اطلاعات ناموفق بود', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      notify(okText);
      await load();
    } catch (err) {
      notify(err?.response?.data?.message || 'عملیات ناموفق بود', 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitDecision = async () => {
    const { request, action } = decision;
    await run(
      () => (action === 'approve' ? approveUnlockRequestApi(request.id, adminNote) : rejectUnlockRequestApi(request.id, adminNote)),
      action === 'approve' ? 'مسدودیت برداشته شد' : 'درخواست رد شد'
    );
    setDecision(null);
    setAdminNote('');
  };

  if (loading && !overview) return <div className="py-16 text-center text-slate-400">در حال بارگذاری...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-text-dark">مرکز امنیت</h1>
        <p className="mt-1 text-sm text-slate-400">
          حساب‌های مسدودشده، درخواست‌های رفع مسدودیت و تلاش‌های ناموفق اخیر ورود
        </p>
      </div>

      {message.text && (
        <div className={`rounded-xl p-3 text-sm font-bold ${message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
          {message.text}
        </div>
      )}

      {overview && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label="حساب مسدود" value={overview.lockedUsers} tone="bg-red-50 text-red-600" />
          <Stat label="درخواست رفع مسدودیت" value={overview.pendingRequests} tone="bg-amber-50 text-amber-600" />
          <Stat label="دارای ورود دو مرحله‌ای" value={`${overview.withTwoFactor}/${overview.totalUsers}`} tone="bg-emerald-50 text-emerald-600" />
          <Stat label="مدیر بدون ۲FA" value={overview.adminsWithoutTwoFactor} tone="bg-slate-100 text-slate-600" />
          <Stat label="ورود ناموفق (۲۴ ساعت)" value={overview.failedLast24h} tone="bg-blue-50 text-blue-600" />
        </div>
      )}

      {/* ---------- requests ---------- */}
      <section className="rounded-3xl border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-lg font-black">درخواست‌های رفع مسدودیت</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
              <tr>
                {['کاربر', 'نقش', 'پیام کاربر', 'زمان', 'وضعیت', 'عملیات'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requests.map((r) => (
                <tr key={r.id} className="hover:bg-emerald-50/50">
                  <td className="px-4 py-3">
                    <div className="font-bold">{r.user?.fullName}</div>
                    <div className="text-xs text-slate-400" dir="ltr">{r.user?.email}</div>
                  </td>
                  <td className="px-4 py-3">{ROLE[r.user?.role] || r.user?.role}</td>
                  <td className="max-w-[240px] px-4 py-3 text-xs text-slate-500">{r.message || '—'}</td>
                  <td className="px-4 py-3 text-xs">{fmt(r.createdAt)}</td>
                  <td className="px-4 py-3"><StatusBadge status={REQ_BADGE[r.status]} label={REQ_STATUS[r.status]} /></td>
                  <td className="px-4 py-3">
                    {r.status === 'PENDING' ? (
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setDecision({ request: r, action: 'approve' })} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600">
                          تأیید و آزادسازی
                        </button>
                        <button type="button" onClick={() => setDecision({ request: r, action: 'reject' })} className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100">
                          رد
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">{r.adminNote || '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
              {requests.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">درخواستی ثبت نشده است</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------- locked users ---------- */}
      <section className="rounded-3xl border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-lg font-black">حساب‌های مسدود</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="border-b border-slate-200 bg-gradient-to-r from-red-50 to-white">
              <tr>
                {['کاربر', 'باشگاه', 'زمان مسدود شدن', 'عملیات'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {locked.map((u) => (
                <tr key={u.id} className="hover:bg-red-50/40">
                  <td className="px-4 py-3">
                    <div className="font-bold">{u.fullName}</div>
                    <div className="text-xs text-slate-400" dir="ltr">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">{u.club?.name || '—'}</td>
                  <td className="px-4 py-3 text-xs">{fmt(u.lockedAt)}</td>
                  <td className="px-4 py-3">
                    <button type="button" disabled={busy} onClick={() => run(() => unlockUserApi(u.id), 'حساب آزاد شد')} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600 disabled:opacity-50">
                      آزادسازی
                    </button>
                  </td>
                </tr>
              ))}
              {locked.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">حساب مسدودی وجود ندارد 👌</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------- failed logins ---------- */}
      <section className="rounded-3xl border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-lg font-black">آخرین تلاش‌های ناموفق ورود</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="border-b border-slate-200">
              <tr>
                {['کاربر', 'علت', 'IP', 'زمان', 'بازنشانی ۲FA'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="px-4 py-3">
                    <div className="font-bold">{l.user?.fullName}</div>
                    <div className="text-xs text-slate-400" dir="ltr">{l.user?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{l.reason === 'BAD_2FA_CODE' ? 'کد دو مرحله‌ای اشتباه' : 'رمز اشتباه'}</td>
                  <td className="px-4 py-3 font-mono text-xs" dir="ltr">{l.ipAddress || '—'}</td>
                  <td className="px-4 py-3 text-xs">{fmt(l.createdAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => window.confirm(`ورود دو مرحله‌ای «${l.user?.fullName}» بازنشانی شود؟`) && run(() => resetUserTwoFactorApi(l.user.id), 'ورود دو مرحله‌ای بازنشانی شد')}
                      className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                    >
                      بازنشانی ۲FA
                    </button>
                  </td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">موردی ثبت نشده است</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs leading-6 text-slate-400">
          اگر کاربری گوشی خود را گم کرده، با «بازنشانی ۲FA» می‌توانید ورود دو مرحله‌ای او را حذف کنید تا هنگام ورود بعدی دوباره فعال کند.
        </p>
      </section>

      <Modal
        open={Boolean(decision)}
        onClose={() => setDecision(null)}
        title={decision?.action === 'approve' ? 'رفع مسدودیت حساب' : 'رد درخواست'}
        size="md"
      >
        {decision && (
          <div className="space-y-4">
            <p className="text-sm leading-7 text-slate-600">
              {decision.action === 'approve'
                ? `حساب «${decision.request.user?.fullName}» آزاد می‌شود و می‌تواند دوباره وارد شود. مطمئن شوید درخواست از طرف خود کاربر است.`
                : 'درخواست رد می‌شود و حساب همچنان مسدود می‌ماند.'}
            </p>
            <textarea
              rows={3}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="یادداشت مدیر (اختیاری)"
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none focus:border-emerald-500"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDecision(null)}>انصراف</Button>
              <GradientButton onClick={submitDecision} disabled={busy}>
                {busy ? 'در حال انجام...' : decision.action === 'approve' ? 'تأیید و آزادسازی' : 'رد درخواست'}
              </GradientButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
